import { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  useAdminAttendance,
  useAdminLeave,
  useAdminUsers,
  useHolidays,
  useOrgCalendar,
} from "@/api/queries";
import { useSession } from "@/api/session";
import { PageHeader } from "@/app/page-header";
import { NotificationsPanel } from "@/components/notifications-panel";
import { CategoryBarChart, StackedBarChart, StatTile, TrendChart } from "@/components/charts";
import { STATUS_SERIES_COLOR } from "@/components/charts/stacked-bar-chart";
import { Avatar } from "@/components/ui/avatar";
import { Badge, FiledLateBadge, StatusBadge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Panel, PanelHeader, SectionHeading } from "@/components/ui/panel";
import { EmptyState, SkeletonTable } from "@/components/ui/states";
import {
  attendanceRate,
  countBy,
  pendingFirstLogin,
  tallyByDay,
  tallyByTeam,
} from "@/lib/analytics";
import { daysUntil, holidayMap, leaveMap, nextAnniversary, shiftDate, todayStr } from "@/lib/calendar";
import { formatDate, formatTimestampDate } from "@/lib/format";
import { leaveLabel } from "@/lib/labels";
import { WfhWeekCharts } from "@/pages/shared/wfh-charts";

/** Last 14 calendar days, which is enough to read a trend without a filter. */
function recentRange() {
  const end = todayStr();
  return { start: shiftDate(end, -13), end };
}

export function HrOverviewPage() {
  const user = useSession((s) => s.user);
  const { start, end } = useMemo(recentRange, []);

  const users = useAdminUsers();
  const attendance = useAdminAttendance(start, end);
  const leave = useAdminLeave();
  const holidays = useHolidays();
  const calendar = useOrgCalendar();

  // HR & Admin are employees of the company too, so they belong in headcount,
  // analytics and the directory alongside everyone else.
  const staff = users.data ?? [];
  const holidayNames = useMemo(() => holidayMap(holidays.data), [holidays.data]);

  // Approved leave for the same range, indexed `${user_id}|${date}`. The range
  // endpoint has always sent this; the tiles below simply did not read it, so a
  // sanctioned absence counted as a no-show and this page reported a lower rate
  // than HR Attendance did for the same people over the same dates.
  const leaveDaysByPerson = useMemo(() => leaveMap(attendance.data?.leave), [attendance.data?.leave]);

  const dayTallies = useMemo(
    () =>
      attendance.data
        ? tallyByDay(
            attendance.data.users,
            attendance.data.records,
            start,
            end,
            holidayNames,
            leaveDaysByPerson,
          )
        : [],
    [attendance.data, start, end, holidayNames, leaveDaysByPerson],
  );

  const teamTallies = useMemo(
    () =>
      attendance.data
        ? tallyByTeam(
            attendance.data.users,
            attendance.data.records,
            start,
            end,
            holidayNames,
            leaveDaysByPerson,
          )
        : [],
    [attendance.data, start, end, holidayNames, leaveDaysByPerson],
  );

  const rate = attendanceRate(dayTallies);
  const pendingLeave = (leave.data ?? []).filter((r) => r.status === "pending");
  const awaitingFirstLogin = pendingFirstLogin(users.data ?? []);
  const headcountByTeam = useMemo(() => countBy(staff, (u) => u.team_name), [staff]);

  const upcomingCelebrations = useMemo(() => {
    const list: { id: string; name: string; kind: string; daysAway: number }[] = [];
    for (const person of calendar.data?.people ?? []) {
      if (person.date_of_birth) {
        const date = nextAnniversary(person.date_of_birth);
        list.push({
          id: `b-${person.id}`,
          name: person.name ?? "Unnamed",
          kind: "Birthday",
          daysAway: daysUntil(date),
        });
      }
    }
    return list.filter((e) => e.daysAway <= 14).sort((a, b) => a.daysAway - b.daysAway).slice(0, 4);
  }, [calendar.data?.people]);

  const trendData = dayTallies.map((tally) => ({
    day: formatDate(tally.date, "d MMM") ?? tally.date,
    present: tally.complete,
  }));

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        eyebrow="Org-wide"
        title={`Welcome, ${user?.name?.split(" ")[0] ?? "there"}`}
        sub="Everything below spans the whole organisation. Nothing here needs an individual employee's account to be opened."
        actions={
          <Button asChild variant="primary" iconLeft="add-person">
            <Link to="/hr/accounts">Create an account</Link>
          </Button>
        }
      />

      <NotificationsPanel />

      <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatTile
          label="People on the system"
          value={staff.length}
          icon="team"
          loading={users.isLoading}
          meta={`${countBy(staff, (u) => u.team_name).length} teams`}
        />
        <StatTile
          label="Full days, last 14 days"
          value={rate === null ? "—" : `${rate}%`}
          icon="attendance"
          loading={attendance.isLoading}
          meta={rate === null ? "No attendance recorded yet" : "of all working-day slots"}
        />
        <StatTile
          label="Leave awaiting a decision"
          value={pendingLeave.length}
          icon="leave"
          loading={leave.isLoading}
          meta="Decided by each person's manager"
        />
        <StatTile
          label="Yet to set a password"
          value={awaitingFirstLogin.length}
          icon="lock"
          loading={users.isLoading}
          meta={awaitingFirstLogin.length === 0 ? "Everyone is set up" : "Still on a one-time password"}
        />
      </div>

      <div className="grid gap-4 xl:grid-cols-2">
        <TrendChart
          title="Full days recorded"
          note="Last 14 days, org-wide, excluding weekly offs and company holidays"
          data={trendData}
          xKey="day"
          series={[{ key: "present", label: "People with a full day" }]}
          area
          loading={attendance.isLoading}
          {...(attendance.error ? { error: attendance.error.message } : {})}
          emptyBody="No attendance has been recorded in this window yet."
        />

        <CategoryBarChart
          title="Headcount by team"
          note="Every account on the system, by the team it is mapped to"
          data={headcountByTeam}
          valueHeader="People"
          loading={users.isLoading}
          {...(users.error ? { error: users.error.message } : {})}
          emptyBody="No accounts have been created yet."
        />
      </div>

      <StackedBarChart
        title="Attendance by team"
        note="Last 14 working days. Counts each person-day, not each person."
        data={teamTallies as unknown as Record<string, string | number>[]}
        categoryKey="team"
        orientation="horizontal"
        series={[
          { key: "complete", label: "Full day", color: STATUS_SERIES_COLOR.complete },
          { key: "half", label: "Half day", color: STATUS_SERIES_COLOR.half },
          { key: "absent", label: "No record", color: STATUS_SERIES_COLOR.absent },
        ]}
        loading={attendance.isLoading}
        {...(attendance.error ? { error: attendance.error.message } : {})}
        height={Math.max(200, teamTallies.length * 44)}
        emptyBody="No attendance has been recorded in this window yet."
      />

      {/* How much of the organisation is working from home, and whether that is
          changing. On the overview rather than only on the leave page because it
          is a pattern to watch, not a queue to clear. */}
      <SectionHeading
        title="Work from home"
        note="Approved days only, org-wide, counted the way leave is: weekends and company holidays inside a request are not days worked from home."
        actions={
          <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
            <Link to="/hr/leave">Review requests</Link>
          </Button>
        }
      />
      <WfhWeekCharts scopeLabel="the organisation" />

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel className="min-w-0">
          <PanelHeader
            title="Leave awaiting a decision"
            note="Read-only here. Each request is decided by that person's own manager."
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/hr/leave">All leave</Link>
              </Button>
            }
          />
          {leave.isLoading ? (
            <SkeletonTable rows={4} cols={3} />
          ) : pendingLeave.length === 0 ? (
            <EmptyState
              icon="check-circle"
              title="Nothing pending"
              body="Every leave request across the organisation has been actioned."
            />
          ) : (
            <ul className="divide-y divide-line">
              {pendingLeave.slice(0, 5).map((request) => (
                <li key={request.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={request.employee_name} size="sm" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-medium text-ink">
                      {request.employee_name ?? "Unnamed"}
                    </p>
                    <p className="truncate text-xs text-muted">
                      {leaveLabel(request.leave_type)} · {formatDate(request.start_date, "d MMM")}
                      {request.start_date !== request.end_date &&
                        ` to ${formatDate(request.end_date, "d MMM")}`}
                    </p>
                  </div>
                  <FiledLateBadge request={request} />
                  <StatusBadge status={request.status} />
                </li>
              ))}
            </ul>
          )}
        </Panel>

        <Panel className="min-w-0">
          <PanelHeader
            title="Coming up"
            note="Next 14 days"
            actions={
              <Button asChild variant="ghost" size="sm" iconRight="arrow-right">
                <Link to="/hr/org-calendar">Org Calendar</Link>
              </Button>
            }
          />
          {calendar.isLoading ? (
            <SkeletonTable rows={4} cols={2} />
          ) : upcomingCelebrations.length === 0 ? (
            <EmptyState
              icon="calendar"
              title="Nothing in the next 14 days"
              body="Birthdays appear here once people have entered their date of birth during account setup."
            />
          ) : (
            <ul className="divide-y divide-line">
              {upcomingCelebrations.map((event) => (
                <li key={event.id} className="flex items-center gap-3 px-4 py-3">
                  <Avatar name={event.name} size="sm" />
                  <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink">
                    {event.name}
                  </span>
                  <Badge tone="accent" icon="birthday">
                    {event.daysAway === 0 ? "Today" : `in ${event.daysAway}d`}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Panel>
      </div>

      {awaitingFirstLogin.length > 0 && (
        <Panel>
          <PanelHeader
            title="Accounts still on a one-time password"
            note="These people have not signed in and set their own password yet."
            actions={
              <Button asChild variant="secondary" size="sm" iconRight="arrow-right">
                <Link to="/hr/accounts">User Accounts</Link>
              </Button>
            }
          />
          <ul className="divide-y divide-line">
            {awaitingFirstLogin.slice(0, 6).map((account) => (
              <li key={account.id} className="flex items-center gap-3 px-4 py-2.5">
                <Avatar name={account.name} size="sm" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-ink">{account.name ?? account.email}</p>
                  <p className="truncate font-mono text-xs text-muted">{account.email}</p>
                </div>
                <span className="hidden shrink-0 text-xs text-muted sm:block">
                  Created {formatTimestampDate(account.created_at, "d MMM")}
                </span>
                <Badge tone="warn" icon="lock">
                  Pending
                </Badge>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <p className="text-xs text-muted">
        Attendance window: {formatDate(start)} to {formatDate(end)} · today is {formatDate(todayStr())}
      </p>
    </div>
  );
}
